import { Component, OnInit } from '@angular/core';
import { CommonModule } from '@angular/common';
import { AgGridAngular } from 'ag-grid-angular';
import { agGridThemes } from '../../../@shared/ag-grid-themes';
import { deriveDynamicColumnDefs } from '../../../@shared/ag-grid-columns';
import { GridCopyMenuComponent } from '../../../@shared/grid-copy-menu/grid-copy-menu.component';
import { CopySourceButtonComponent } from '../../../@shared/copy-source-button/copy-source-button.component';
import {
  InstrumentDetailsService,
  InstrumentDetail,
  InstrumentStack,
} from '../../../@services/instrument-details.service';

// Instrument Details pane — read-only AG-Grid viewer over the
// LSEG:INSTRUMENT_DETAILS Redis hash. Columns are DERIVED from the
// rows (per Mike 2026-07-17 — nothing hardwired in common-data
// grids). Both stacks return the same {ok, count, rows} envelope;
// switcher just flips the URL prefix.
@Component({
  standalone: true,
  selector: 'app-instrument-details',
  imports: [AgGridAngular, CommonModule, GridCopyMenuComponent, CopySourceButtonComponent],
  templateUrl: './instrument-details.component.html',
})
export class InstrumentDetailsComponent implements OnInit {
  rows: InstrumentDetail[] = [];
  columnDefs: any[] = [];
  loading = false;
  loadedAt: string | null = null;

  // v2 = web-backend-srvr-1 (native Redis); v1 = ws (via lseg-service).
  stack: InstrumentStack = 'v2';

  readonly stacks: { key: InstrumentStack; label: string; hint: string }[] = [
    { key: 'v2', label: 'v2', hint: 'web-backend-srvr-1 (native Redis)' },
    { key: 'v1', label: 'v1', hint: 'ws / phoenix5 (via lseg-service)' },
  ];

  theme = agGridThemes.dark;
  grid: any;

  constructor(private svc: InstrumentDetailsService) {
    this.init_grid();
  }

  async ngOnInit() {
    await this.reload();
  }

  async reload() {
    this.loading = true;
    try {
      this.rows = await this.svc.listAll(this.stack);
      this.columnDefs = deriveDynamicColumnDefs(this.rows);
      this.loadedAt = new Date().toISOString();
      const api = this.grid?.api;
      if (api?.autoSizeAllColumns) {
        setTimeout(() => api.autoSizeAllColumns(false), 0);
      }
    } finally {
      this.loading = false;
    }
  }

  async setStack(s: InstrumentStack) {
    if (s === this.stack) return;
    this.stack = s;
    await this.reload();
  }

  init_grid() {
    this.grid = {
      options: {
        context: this,
        suppressCellFocus: true,
        rowSelection: {
          mode: 'multiRow',
          checkboxes: true,
          headerCheckbox: true,
          enableClickSelection: false,
        },
        defaultColDef: {
          floatingFilter: true,
          filter: true,
          sortable: true,
          resizable: true,
          cellStyle: { 'text-align': 'left' },
        },
        // Column tool panel — drag-to-hide/reorder + pin per column.
        sideBar: {
          toolPanels: [
            { id: 'columns', labelDefault: 'Columns', labelKey: 'columns', iconKey: 'columns', toolPanel: 'agColumnsToolPanel' },
          ],
        },
        autoSizeStrategy: { type: 'fitCellContents', skipHeader: false },
        onGridReady: (e: any) => { this.grid.api = e.api; },
        onFirstDataRendered: (e: any) => e.api.autoSizeAllColumns(false),
      },
      statusBar: {
        statusPanels: [
          { statusPanel: 'agTotalRowCountComponent', align: 'left' },
          { statusPanel: 'agFilteredRowCountComponent', align: 'left' },
          { statusPanel: 'agSelectedRowCountComponent', align: 'left' },
        ],
      },
    };
  }
}
